import React, { useEffect, useMemo, useState } from 'react';
import { Search, SlidersHorizontal, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';

const INITIAL_LOAD = 24;
const LOAD_MORE_COUNT = 12;

const categories = [
  { id: 'all', name: 'All' },
  { id: 'prewedding', name: 'Pre-Wedding' },
  { id: 'ceremony', name: 'Ceremony' },
  { id: 'couple', name: 'Couple Shots' },
  { id: 'candid', name: 'Candid' },
  { id: 'portraits', name: 'Portraits' },
  { id: 'details', name: 'Details' }
];

const hashString = (value) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
};

const createSeededRandom = (seedValue) => {
  let seed = seedValue || 1;
  return () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    return seed / 4294967296;
  };
};

const shuffleImages = (items, seedKey) => {
  const next = [...items];
  const random = createSeededRandom(hashString(seedKey));

  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }

  return next;
};

const getColumnCount = (width) => {
  if (width >= 1024) return 4;
  if (width >= 768) return 3;
  return 2;
};

const getImageRatio = (image, ratios) => {
  const fromState = ratios[image.id];
  if (fromState && Number.isFinite(fromState) && fromState > 0) {
    return fromState;
  }
  return 1.25;
};

const buildBalancedColumns = (items, columnCount, ratios) => {
  const columns = Array.from({ length: columnCount }, () => []);
  const heights = Array.from({ length: columnCount }, () => 0);

  items.forEach((image) => {
    const ratio = getImageRatio(image, ratios);
    const targetIndex = heights.indexOf(Math.min(...heights));
    columns[targetIndex].push(image);
    heights[targetIndex] += ratio;
  });

  return columns;
};

const RealMosaicGallery = ({ images, onImageClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredImages, setFilteredImages] = useState(images);
  const [displayCount, setDisplayCount] = useState(INITIAL_LOAD);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [gallerySeed] = useState(() => `${Date.now()}`);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [imageRatios, setImageRatios] = useState({});

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let nextFiltered = images;

    if (selectedCategory !== 'all') {
      nextFiltered = nextFiltered.filter((img) => img.category === selectedCategory);
    }

    if (searchTerm) {
      nextFiltered = nextFiltered.filter((img) =>
        img.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredImages(
      shuffleImages(
        nextFiltered,
        `${gallerySeed}-${selectedCategory}-${searchTerm}-${nextFiltered.map((item) => item.id).join('|')}`
      )
    );
    setDisplayCount(INITIAL_LOAD);
  }, [selectedCategory, searchTerm, images, gallerySeed]);

  const displayedImages = filteredImages.slice(0, displayCount);
  const hasMore = displayCount < filteredImages.length;
  const imageUrls = useMemo(() => filteredImages.map((img) => img.url), [filteredImages]);
  const columnCount = getColumnCount(viewportWidth);

  const columns = useMemo(
    () => buildBalancedColumns(displayedImages, columnCount, imageRatios),
    [displayedImages, columnCount, imageRatios]
  );

  const handleLoadMore = () => {
    setIsLoading(true);
    setDisplayCount((prev) => Math.min(prev + LOAD_MORE_COUNT, filteredImages.length));
    setIsLoading(false);
  };

  const handleImageLoad = (imageId, event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (!naturalWidth || !naturalHeight) return;
    const ratio = naturalHeight / naturalWidth;

    setImageRatios((prev) => {
      if (prev[imageId] === ratio) return prev;
      return { ...prev, [imageId]: ratio };
    });
  };

  return (
    <section id="gallery" className="bg-gray-50 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 space-y-4 text-center">
          <span className="text-sm font-medium uppercase tracking-widest text-gray-500">Gallery</span>
          <h2 className="text-5xl font-bold text-gray-900 md:text-6xl">Wedding Gallery</h2>
          <p className="mx-auto max-w-2xl text-lg text-gray-600">
            A collection of our finest wedding photography moments
          </p>
        </div>

        <div className="mb-12 space-y-4">
          <div className="relative mx-auto max-w-md">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-gray-300 bg-white py-6 pl-10 text-base focus:border-gray-900"
            />
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="bg-white md:hidden"
            >
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              Filters
            </Button>

            <div className={`${isFilterOpen ? 'flex' : 'hidden md:flex'} flex-wrap justify-center gap-3`}>
              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? 'default' : 'outline'}
                  onClick={() => setSelectedCategory(category.id)}
                  className={
                    selectedCategory === category.id
                      ? 'bg-gray-900 text-white hover:bg-gray-800'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                  }
                >
                  {category.name}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-8 text-center text-gray-600">
          Showing {displayedImages.length} of {filteredImages.length} photos
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {columns.map((column, columnIndex) => (
            <div key={`column-${columnIndex}`} className="flex flex-col gap-4">
              {column.map((image, index) => (
                <button
                  key={image.id}
                  type="button"
                  className="group relative overflow-hidden rounded-lg bg-gray-200 text-left"
                  onClick={() => onImageClick(image.url, imageUrls)}
                >
                  <img
                    src={image.url}
                    alt={`Gallery ${image.category}`}
                    className="h-auto w-full"
                    loading={columnIndex === 0 && index < 2 ? 'eager' : 'lazy'}
                    decoding="async"
                    fetchPriority={columnIndex === 0 && index === 0 ? 'high' : 'low'}
                    onLoad={(event) => handleImageLoad(image.id, event)}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <span className="rounded-full bg-black/30 px-3 py-1 text-sm font-medium capitalize text-white backdrop-blur-sm">
                        {image.category}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>

        {hasMore && (
          <div className="mt-12 text-center">
            <Button
              onClick={handleLoadMore}
              disabled={isLoading}
              className="bg-gray-900 px-12 py-6 text-lg font-medium text-white transition-colors duration-200 hover:bg-gray-800"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Loading...
                </>
              ) : (
                `Load More Photos (${Math.min(LOAD_MORE_COUNT, filteredImages.length - displayedImages.length)} more)`
              )}
            </Button>
          </div>
        )}

        {filteredImages.length === 0 && (
          <div className="py-20 text-center">
            <p className="text-lg text-gray-500">No photos found matching your criteria</p>
            <Button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
              }}
              variant="outline"
              className="mt-4 bg-white"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default RealMosaicGallery;
